import { setViewModel } from './myBookingSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { NavigateParams } from '../../components/BtNavigate';
import { InputParams } from '../../components/BtInput';
import { ButtonParams } from '../../components/BtButton';
import { validateBookingId } from '../landing/validators';
import { apiGetV2, Failure, getBaseUrl } from '../../support/apiCallHelper';
import moment from 'moment';
import { GET_MISSION_BY_ID } from '../../support/apiConstants';
import {
  Company,
  NoCompany,
  NoPerson,
  NoVehicle,
  NoWeight,
  Person,
  Vehicle,
  Weight,
} from '../../components/BtGroup';
import { BtListViewParams } from '../../components/BtListView';
import { FailedParams } from '../../components/BtFailed';
import { SpinnerParams } from '../../components/BtSpinner';
import { LoadingParams } from '../../components/BtLoading';
import { cancelScheduledMission } from '../gat/gatApi';

export function none(logoUrl: string): MyBookingScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      back: '',
      home: '',
      faq: '',
      support: '',
      booking: '',
      body: '',
      bokkingId: '',
      regno: '',
      search: '',
      info: '',
      bookingResult: '',
      cancel: '',
      status: '',
      noMatch: '',
      weight: '',
      confirmTitle: '',
      yes: '',
      no: '',
      cancelBody: '',
      cancelTitle: '',
      ok: '',
      errorInfo: '',
      image: '',
      phone: '',
      metaTitle: '',
      interrupt: '',
      loading: '',
    },
    state: {
      regno: '',
      cancelToken: '',
      bookingId: '',
      missionId: '',
      date: '',
      time: '',
      from: '',
      to: '',
      vehicleType: '',
      registrationNumber: '',
      brand: '',
      weight: '',
      items: [null],
      showModal: false,
      scroll: false,
    },
    error: {
      status: '',
      body: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl,
      },
    },
  };
}

export namespace MyBookingScreen {
  interface Texts {
    title: string;
    back: string;
    home: string;
    booking: string;
    faq: string;
    support: string;
    search: string;
    body: string;
    bokkingId: string;
    regno: string;
    info: string;
    bookingResult: string;
    cancel: string;
    status: string;
    noMatch: string;
    weight: string;
    confirmTitle: string;
    yes: string;
    no: string;
    cancelBody: string;
    cancelTitle: string;
    ok: string;
    errorInfo: string;
    image: string;
    phone: string;
    metaTitle: string;
    interrupt: string;
    loading: string;
  }

  export interface State {
    cancelToken: string;
    regno: string;
    bookingId: string;
    missionId: string;
    date: string;
    registrationNumber: string;
    brand: string;
    time: string;
    from: string;
    to: string;
    vehicleType: string;
    weight: string;
    items: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ];
    showModal: boolean;
    scroll: boolean;
  }

  export interface booking {
    destination: {
      address: string;
      latitude: string;
      longitude: string;
    };
    id: string;
    price: string;
    registrationNumber: string;
    scheduledAt: string;
    slots: [{ fromTime: string; toTime: string; timezone: string }];
    source: {
      address: string;
      latitude: string;
      longitude: string;
    };
  }

  export interface None {
    type: 'none';
    texts: Texts;
    state: State;
    error: FailedParams;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    greeting: string;
    bookingId: InputParams;
    info: string;
    image: string;
    body: string;
    search: ButtonParams;
    cancel: ButtonParams;
    status: ButtonParams;
    bookingResult: string;
    date: BtListViewParams;
    time: BtListViewParams;
    from: BtListViewParams;
    registrationNumber: BtListViewParams;
    brand: string;
    arrowIcon: string;
    to: BtListViewParams;
    yes: ButtonParams;
    no: ButtonParams;
    confirmTitle: string;
    metaTitle: string;
    spinner: SpinnerParams;
  }

  export interface Failed {
    type: 'failed';
    texts: Texts;
    state: State;
    error: FailedParams;
  }

  export interface Finish {
    type: 'finish';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    title: string;
    ok: ButtonParams;
    body: string;
    image: string;
    phone: string;
    imageSupport: string;
  }

  export type ViewModel = None | Loading | Ready | Failed | Finish;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }
  export function isFailed(viewModel: ViewModel): viewModel is Failed {
    return (viewModel as Failed).type === 'failed';
  }
  export function isFinish(viewModel: ViewModel): viewModel is Finish {
    return (viewModel as Finish).type === 'finish';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    if (isFailed(viewModel)) return viewModel.texts;
    if (isFinish(viewModel)) return viewModel.texts;

    return {
      title: '',
      back: '',
      home: '',
      booking: '',
      faq: '',
      support: '',
      body: '',
      bokkingId: '',
      regno: '',
      search: '',
      info: '',
      bookingResult: '',
      cancel: '',
      status: '',
      noMatch: '',
      weight: '',
      confirmTitle: '',
      yes: '',
      no: '',
      cancelBody: '',
      cancelTitle: '',
      ok: '',
      errorInfo: '',
      image: '',
      phone: '',
      metaTitle: '',
      interrupt: '',
      loading: '',
    };
  }

  function loading(viewModel: ViewModel, state: State, logoUrl: string): Loading {
    const t = i18n.t;
    return {
      type: 'loading',
      texts: {
        title: t('myBookingScreen.title'),
        back: t('image.back'),
        home: t('myBookingScreen.home'),
        booking: t('navigation.booking'),
        faq: t('navigation.faq'),
        support: t('navigation.support'),
        body: t('myBookingScreen.body'),
        bokkingId: t('myBookingScreen.bokkingId'),
        regno: t('myBookingScreen.regno'),
        search: t('myBookingScreen.search'),
        info: t('myBookingScreen.info'),
        bookingResult: t('myBookingScreen.bookingResult'),
        cancel: t('myBookingScreen.cancel'),
        status: t('myBookingScreen.status'),
        noMatch: t('myBookingScreen.noMatch'),
        weight: t('myBookingScreen.weight'),
        confirmTitle: t('myBookingScreen.confirmTitle'),
        yes: t('myBookingScreen.yes'),
        no: t('myBookingScreen.no'),
        cancelBody: '',
        cancelTitle: t('myBookingScreen.cancelTitle'),
        ok: t('myBookingScreen.ok'),
        errorInfo: t('myBookingScreen.errorInfo'),
        image: t('image.ico_my_booking_empty_info'),
        phone: t('supportScreen.phone'),
        metaTitle: t('myBookingScreen.metaTitle'),
        interrupt: t('myBookingScreen.interrupt'),
        loading: t('loading.title'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
      state: state,
    };
  }

  function ready(
    viewModel: ViewModel,
    state: State,
    texts: Texts,
    errorBookingId: string | null,
    logoUrl: string,
  ): Ready {
    // const texts = getTexts(viewModel)
    const t = i18n.t;
    return {
      type: 'ready',
      texts: texts,
      state: state,
      greeting: texts.title,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: t('images.home'), visible: true },
        image: logoUrl,
      },
      bookingId: {
        id: 'bookingId',
        name: 'bookingId',
        title: texts.bokkingId,
        message: errorBookingId ? errorBookingId : '',
        disabled: false,
        value: state.bookingId,
        type: 'number',
      },
      info: texts.info,
      image: texts.image,
      body: texts.body,
      search: { title: texts.search, image: null, visible: true },
      cancel: { title: texts.cancel, image: null, visible: true },
      status: { title: texts.status, image: 'ico_external_link', visible: true },
      bookingResult: texts.bookingResult,
      date: { title: state.date, image: 'ico_calendar_sm', visible: true },
      registrationNumber: {
        title: `${state.registrationNumber}, ${state.brand}`,
        image: 'ico_vehicle_info',
        visible: true,
      },
      brand: state.brand,
      time: { title: state.time, image: 'ico_time_sm', visible: true },
      from: { title: state.from, image: 'ico_position_sm', visible: true },
      arrowIcon: 'ico_route',
      to: { title: state.to, image: 'ico_position_sm', visible: true },
      confirmTitle: texts.confirmTitle,
      yes: { title: texts.yes, image: null, visible: true },
      no: { title: texts.no, image: null, visible: true },
      metaTitle: texts.metaTitle,
      spinner: { interrupt: { title: texts.interrupt, image: '', visible: true } },
    };
  }

  function failed(viewModel: ViewModel, state: State, status: string, statusText: string): Failed {
    const texts = getTexts(viewModel);
    return {
      type: 'failed',
      texts: texts,
      state: state,
      error: {
        status: status,
        body: statusText,
      },
    };
  }

  function finish(viewModel: ViewModel, texts: Texts, state: State, logoUrl: string): Finish {
    return {
      type: 'finish',
      texts: texts,
      state: state,
      title: texts.cancelTitle,
      image: texts.image,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      ok: { title: texts.ok, image: null, visible: true },
      body: texts.cancelBody,
      phone: texts.phone,
      imageSupport: 'ico_support',
    };
  }

  export async function reset(logoUrl: string): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none(logoUrl)));
    };
  }

  export function start(
    viewModel: ViewModel,
    logoUrl: string,
  ): (dispatch: Dispatch<AnyAction>) => void {
    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel, viewModel.state, logoUrl)));
    };
  }

  export async function load(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(ready(viewModel, viewModel.state, viewModel.texts, null, logoUrl)));
    };
  }

  function formateDate(date: string): string {
    return date.substring(0, 10);
  }

  const formatTime = (date: string): string => {
    return moment.utc(date).local().format('HH:mm');
  };

  type GatCancelMissionResult = {
    registrationNumber: string;
    missionDate: string;
    token: string;
    missionId: string;
    brand: string;
    make: string;
    model: string;
  };

  export async function search(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const t = i18n.t;
    const errorBookingId = validateBookingId(viewModel.state.bookingId);

    if (errorBookingId) {
      return (dispatch) => {
        dispatch(
          setViewModel(
            ready(
              viewModel,
              { ...viewModel.state, scroll: false },
              viewModel.texts,
              errorBookingId,
              logoUrl,
            ),
          ),
        );
      };
    } else {
      const gatRsponse = await callGAT(viewModel.state.bookingId);

      return (dispatch) => {
        if (!(gatRsponse instanceof Failure)) {
          const response = gatRsponse as GatCancelMissionResult;

          console.log('gatRsponse', gatRsponse);

          const newState = {
            ...viewModel.state,
            registrationNumber: response.registrationNumber,
            brand: response.brand,
            date: formateDate(response.missionDate),
            time: formatTime(response.missionDate),
            cancelToken: response.token,
            missionId: response.missionId,
            scroll: true,
          };

          dispatch(setViewModel(ready(viewModel, newState, viewModel.texts, null, logoUrl)));
        } else if (gatRsponse.status === 404) {
          const newState: State = {
            ...viewModel.state,
            date: '',
            time: '',
            from: '',
            to: '',
            vehicleType: '',
            weight: '',
            showModal: false,
            scroll: true,
          };

          dispatch(
            setViewModel(
              ready(
                viewModel,
                newState,
                {
                  ...viewModel.texts,
                  info: viewModel.texts.errorInfo,
                  image: t('image.ico_my_booking_no_info'),
                },
                null,
                logoUrl,
              ),
            ),
          );
        } else {
          const resp = gatRsponse as Failure;
          if (resp.statusText === 'AbortError: The user aborted a request.') {
            dispatch(
              setViewModel(ready(viewModel, viewModel.state, viewModel.texts, null, logoUrl)),
            );
          } else {
            dispatch(
              setViewModel(failed(viewModel, viewModel.state, `${resp.status}`, resp.statusText)),
            );
          }
        }
      };
    }
  }

  export async function showModal(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const newState = { ...viewModel.state, showModal: !viewModel.state.showModal, scroll: false };
    return (dispatch) => {
      dispatch(setViewModel(ready(viewModel, newState, viewModel.texts, null, logoUrl)));
    };
  }

  export async function cancel(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const t = i18n.t;
    const errorBookingId = validateBookingId(viewModel.state.bookingId);

    if (errorBookingId) {
      return (dispatch) => {
        dispatch(
          setViewModel(ready(viewModel, viewModel.state, viewModel.texts, errorBookingId, logoUrl)),
        );
      };
    } else {
      const response = await GatDelete(viewModel);

      console.log('response', response);

      return (dispatch) => {
        if (response.status === 404) {
          dispatch(
            setViewModel(
              finish(
                viewModel,
                {
                  ...viewModel.texts,
                  cancelTitle: t('myBookingScreen.errorCancelTitle'),
                  cancelBody: t('myBookingScreen.errorCancelBody'),
                  image: '',
                },
                viewModel.state,
                logoUrl,
              ),
            ),
          );
        } else if (response.status === 200) {
          dispatch(
            setViewModel(
              finish(
                viewModel,
                { ...viewModel.texts, image: t('images.successful'), phone: '' },
                viewModel.state,
                logoUrl,
              ),
            ),
          );
        } else {
          const resp = response as Failure;
          if (resp.statusText === 'AbortError: The user aborted a request.') {
            dispatch(
              setViewModel(ready(viewModel, viewModel.state, viewModel.texts, null, logoUrl)),
            );
          } else {
            dispatch(
              setViewModel(failed(viewModel, viewModel.state, `${resp.status}`, resp.statusText)),
            );
          }
        }
      };
    }
  }

  export async function uppdateState(
    viewModel: ViewModel,
    event: React.ChangeEvent<HTMLInputElement>,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const target = event.target;
    const value = target.value.toUpperCase();

    const newState = { ...viewModel.state, [target.name]: value, scroll: false };
    return async (dispatch) => {
      dispatch(await setViewModel(ready(viewModel, newState, viewModel.texts, null, logoUrl)));
    };
  }

  async function callGAT(shortId: string): Promise<GatCancelMissionResult | Failure> {
    const controller = new AbortController();

    const baseUrl = getBaseUrl();
    const url = `${baseUrl}${GET_MISSION_BY_ID}`;
    const query = `shortId=${encodeURIComponent(shortId)}`;

    try {
      const response = await apiGetV2<GatCancelMissionResult>(url, query, controller);
      return response;
    } catch (e: any) {
      console.log('GAT error:', e.statusCode);
      return new Failure(0, `${e}`);
    }
  }

  export class Call {
    status: number;
    constructor(status: number) {
      this.status = status;
    }
  }

  const controller: AbortController | null = null;
  export async function cancelFetch() {
    if (controller) {
      controller.abort();
    }
  }

  async function GatDelete(viewModel: ViewModel): Promise<Call | Failure> {
    const controller = new AbortController();

    try {
      const response = await cancelScheduledMission({
        token: viewModel.state.cancelToken,
        missionId: viewModel.state.missionId,
        controller,
      });

      if (response instanceof Failure) {
        return response;
      }

      return new Call(200);
    } catch (e: any) {
      console.log('GAT Cancel Error:', e);
      return new Failure(0, e.toString());
    }
  }
}
